/**
 * components/rightbar/ProgressTabBody.tsx —— 任务进展页 tab 正文（kind=`progress`）。
 *
 * 真源：`102:frontend_src/src/components/rightbar/ProgressTabBody.vue`（493 行）——抄形态/尺寸/交互，译 Vue→React+TS。
 * 依据：《动作生命周期语义-收敛重设计方案-20260922》§四 **F-7**：
 *   ① **页身份换代**：`builtin:guide`（「引导页」）→ `builtin:progress`（「任务进展」）；原引导页的
 *      **入口胶囊体系整件退役** ⇒ 本页**不放任何产物入口**（承大王 v1.7 订正「页内取消产物按钮」）。
 *   ② **页内形态＝动作按时序**：数据源＝会话事件窗口（**单源**，零新数据通道）；条目＝**节点域 7 名事件**
 *      （`NODE_DELIVERY_EVENT_TYPES`，与 node-delivery 同源同名，禁自造）；**序＝事件时刻升序**；
 *      行＝时刻＋节点名＋动作名＋载荷。
 *   ③ **★20260925 编排步骤聚合段**（`orch_step_done`，落点＝规范表 `panel`＝只进右栏聚合）：
 *      折叠头＝「编排步骤 N 步 · 最近 HH:MM」一行摘要；点开列**最近 K 条**（`ORCH_STEP_MAX_ROWS`=20）
 *      ＋「共 N 步」全量计数（截断**明说**）；展开区封顶内滚（`--xg-max-h-output`；卡根不写 overflow-y）；
 *      默认折叠。🔴 边界：不加进任何 `definitions/` 认领集、不改 `NODE_DELIVERY_EVENT_TYPES`/状态机；
 *      `message` JSON 解析只在纯函数（102 `rightbar/orchSteps.js`）做一次——组件零解析。
 *      空态：无 `orch_step_done` ⇒ 该段**整段不渲染**（诚实缺席）。
 * 性质：**读面**（只读单源、零状态载体）：不持链级事实，仅把节点域既有事实按时刻投影。
 * 边界（组件三戒）：不创建 tab、不改布局、不发请求、不解析 `message` 原文、不虚构时刻（无来源 ⇒ 留空，A7）。
 *
 * Vue→React 差异点（登记）：
 *   ① 零取数（任务书 §2-3）：源件 `useSessionScope().window` 订阅／回填／事件过滤投影
 *      （`NODE_DELIVERY_EVENT_TYPES`＋`momentOf`/`nodeNameOf`/`momentLabelOf`，102 `assembly/definitions/node-delivery.js`；
 *      `projectOrchSteps`，102 `rightbar/orchSteps.js`）**移宿主**（随包未投，禁凭记忆自造词表）
 *      ⇒ 本件吃 `items`（源件 computed 行同形，含 `sortAt`）＋`orch`（`projectOrchSteps` 产物同形）。
 *      排序判法（升序＋无时刻保序排末）**留在本件**逐字（comparator 同源件），双排幂等。
 *   ② `utils/sessionTime.js`（随包未投）⇒ `normalizeIso`/`parseSessionTime`/`formatSessionTime` **逐字译入本件**
 *      （102 同名件原文；时刻域单一事实源收编归 main）；`ORCH_STEP_MAX_ROWS` 同因逐字译入（102 orchSteps.js）。
 *   ③ `orchOpen` ref ⇒ useState；两路订阅/回填（`subscribe`/`onWindowChange`/`resumeWindow`）随取数移宿主。
 */
import React, { useState } from "react";
import type { TabBodyProps } from "./TabBodySeat";

// ── 时刻域纯函数（逐字译自 102 `utils/sessionTime.js`；要改展示口径只改这一处） ──────────

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** 后端时刻形制归一（asyncpg：空格分隔＋6 位小数 ⇒ 空格→T＋小数截到 3 位；不动时刻、不补时区） */
export function normalizeIso(v: unknown): string {
  if (typeof v !== "string") return "";
  const s = v.trim();
  if (!s) return "";
  return s.replace(" ", "T").replace(/(\.\d{3})\d+/, "$1");
}

/** 时刻解析（排序/比较用）：Date/number 直通；字符串先归一再解析；非法/空 ⇒ NaN（禁假装成 0/现在） */
export function parseSessionTime(v: string | Date | number | null | undefined): number {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const s = normalizeIso(v);
  if (!s) return NaN;
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? NaN : t;
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * 展示文案（同日 ⇒ `HH:mm`／昨天 ⇒ `昨天`／2–6 天前 ⇒ `N 天前`／同年 ⇒ `M月D日`／跨年 ⇒ `YYYY-MM-DD`；
 * 非法输入 ⇒ ''——如实留空，不假装）。
 */
export function formatSessionTime(iso: string | Date | number | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = startOfDay(d) === startOfDay(now);
  if (sameDay) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (dayDiff === 1) return "昨天";
  if (dayDiff >= 2 && dayDiff <= 6) return `${dayDiff} 天前`;
  if (d.getFullYear() === now.getFullYear()) return `${d.getMonth() + 1}月${d.getDate()}日`;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** 展开区最多列出的**最近**条数（K）——逐字译自 102 `rightbar/orchSteps.js::ORCH_STEP_MAX_ROWS` */
export const ORCH_STEP_MAX_ROWS = 20;

/** 进度行（承源件 `items` computed 产物同形） */
export interface ProgressRow {
  key: string;
  node: string;
  label: string;
  atText: string;
  action?: string;
  errorCode?: string;
  /** ★F3：断点原因／纯文本消息（与对话流 NodeDeliveryRow 同源同键） */
  detail?: string;
  /** 排序键（＝`parseSessionTime(moment.at)`；缺省 ⇒ NaN ⇒ 保序排末，不虚构时刻） */
  sortAt?: number;
}

/** 编排步骤行（承 102 `projectOrchSteps` 行同形） */
export interface OrchStepRow {
  stepNo: number | null;
  at: string | number | Date | null;
  tool?: string | null;
  ok: boolean | null;
  error?: string | null;
  hasPayload: boolean;
  degraded: boolean;
}

/** 编排步骤聚合（承 102 `projectOrchSteps` 返回同形：count＝全量真值；rows＝最近 K 条） */
export interface OrchSummary {
  count: number;
  rows: OrchStepRow[];
  maxRows: number;
  truncated: boolean;
  degraded: number;
  latestAt?: string | number | Date | null;
}

/** 运行态行（★PFK2R·L-PF-B5 实装位：内核读面行**原值透传**；来源＝`proj.combinators.runtime_state()`） */
export interface OrchRuntimeRow {
  /** 对象锚三源（账行判据列逐字）：`wf.job`／`wf.step`／`wf.wait`（等待项） */
  source: "wf.job" | "wf.step" | "wf.wait";
  /** 行标识原值（job_id／step_id／wait_id；缺 ⇒ null，不造值） */
  ref: string | null;
  /** 内核状态**原值**（不换算、不翻译；等待项开行＝读面语义 `"open"`） */
  state: string | null;
  runId: string | null;
  /** 等待项 `label` 原值（仅 `wf.wait` 行） */
  label: string | null;
}

/** 运行态聚合（在跑／在等；值＝内核原值，计数由原值派生；判定词面＝内核词域） */
export interface OrchRuntime {
  /** 在跑计数（`state` 原值＝`running` 的行数；字面属 `kernel.state.machine.JOB_STATES`） */
  running: number;
  /** 在等计数（等待项开行数；`open_waits` 内核语义） */
  waiting: number;
  rows: OrchRuntimeRow[];
  /** 读面缺口如实登记（如 `wf.job` 列表读面缺）——原样呈现，不吞 */
  gaps: string[];
}

export interface ProgressTabBodyProps extends TabBodyProps {
  /** 节点域动作行（宿主投影：7 名事件过滤＋映射；本件只排与渲染） */
  items?: ProgressRow[];
  /** 编排步骤聚合（宿主经 projectOrchSteps 投影；null/缺省 ⇒ 整段不渲染——诚实缺席） */
  orch?: OrchSummary | null;
  /** ★PFK2R·L-PF-B5 运行态（在跑/在等；宿主经 projectOrchRuntime 投影内核读面产物）；null/缺省 ⇒ 整段不渲染——诚实缺席 */
  runtime?: OrchRuntime | null;
}

/** 编排段缺省（零条＝整段不渲染） */
const EMPTY_ORCH: OrchSummary = {
  count: 0,
  rows: [],
  maxRows: ORCH_STEP_MAX_ROWS,
  truncated: false,
  degraded: 0,
  latestAt: null,
};

/** 任务进展页 tab 正文 */
export function ProgressTabBody(props: ProgressTabBodyProps): React.ReactElement {
  const { info } = props;
  /** 会话 id 来自座位输入面（本页是**页类型**，会话由停靠面宿主注入，非 tab 参数） */
  const sessionId = info.sessionId || null;
  const orch = props.orch ?? EMPTY_ORCH;
  /** 编排段展开态（**默认折叠**：566 行量级 ⇒ 一行摘要先给，点开才铺） */
  const [orchOpen, setOrchOpen] = useState(false);

  /** 条目列表：按事件时刻升序；无可用时刻者（NaN）**保序排末**——不虚构时刻（A7；comparator 逐字承源件） */
  const items = (props.items ?? [])
    .map((row, index) => [row, index] as const)
    .sort((a, b) => {
      const ta = Number.isNaN(a[0].sortAt ?? NaN) ? Infinity : (a[0].sortAt as number);
      const tb = Number.isNaN(b[0].sortAt ?? NaN) ? Infinity : (b[0].sortAt as number);
      return ta - tb || a[1] - b[1];
    })
    .map(([row]) => row);

  /** 时刻 → 展示文案（`parseSessionTime` 单源归一；取不到 ⇒ 空串，不虚构时刻） */
  function fmtAt(v: string | number | Date | null | undefined): string {
    const ms = parseSessionTime(v);
    if (Number.isNaN(ms)) return "";
    return formatSessionTime(new Date(ms));
  }

  /** 最近时刻文案（无来源 ⇒ 空串 ⇒ 摘要不写「· 最近 …」，如实缺席） */
  const orchLatestText = fmtAt(orch.latestAt ?? null);

  /** 工具名文案（三类如实分列：未解析／无载荷／未记名——不含糊成一句「未知」） */
  function toolTextOf(row: OrchStepRow): string {
    if (row.degraded) return "（载荷未解析）";
    if (!row.hasPayload) return "（无载荷）";
    return row.tool || "（未记工具名）";
  }

  return (
    <div className="xg-progress-tab" data-testid="rightbar-progress">
      {/* 无会话作用域 ⇒ 如实说明（不画空列表、不假装有内容） */}
      {!sessionId ? (
        <p className="xg-progress-tab__hint" data-testid="rightbar-progress-no-session">
          未选择会话（任务进展按会话作用域）
        </p>
      ) : (
        <>
          {/* ★PFK2R·L-PF-B5 运行态段（在跑/在等；值＝内核读面原值透传）；缺省 ⇒ 整段不渲染（诚实缺席） */}
          {props.runtime ? (
            <section className="xg-progress-tab__runtime" data-testid="rightbar-progress-runtime">
              <p className="xg-progress-tab__runtime-head" data-testid="rightbar-progress-runtime-summary">
                运行态：在跑 {props.runtime.running} · 在等 {props.runtime.waiting}
              </p>
              {props.runtime.rows.length > 0 ? (
                <ul className="xg-progress-tab__runtime-list" data-testid="rightbar-progress-runtime-list">
                  {props.runtime.rows.map((r, i) => (
                    <li
                      key={`${i}:${r.source}:${r.ref}`}
                      className="xg-progress-tab__runtime-row"
                      data-runtime-source={r.source}
                      data-runtime-ref={r.ref || ""}
                    >
                      <span className="xg-progress-tab__runtime-src">{r.source}</span>
                      <span className="xg-progress-tab__runtime-ref">{r.ref || "（无标识）"}</span>
                      <span className="xg-progress-tab__runtime-state">{r.state || "（无状态）"}</span>
                      {r.label ? (
                        <span className="xg-progress-tab__runtime-label" title={r.label}>
                          {r.label}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {props.runtime.gaps.length > 0 ? (
                <p className="xg-progress-tab__runtime-gaps" data-testid="rightbar-progress-runtime-gaps">
                  （读面缺口：{props.runtime.gaps.join("；")}）
                </p>
              ) : null}
            </section>
          ) : null}

          {/* ★20260925 编排步骤聚合段（落点＝规范表 panel）；无 orch_step ⇒ 整段不渲染 */}
          {orch.count > 0 ? (
            <section className="xg-progress-tab__orch" data-testid="rightbar-progress-orch">
              <button
                type="button"
                className="xg-progress-tab__orch-head xg-touch-44"
                data-testid="rightbar-progress-orch-toggle"
                aria-expanded={orchOpen ? "true" : "false"}
                onClick={() => setOrchOpen(!orchOpen)}
              >
                <span className="xg-progress-tab__orch-caret" aria-hidden="true">
                  {orchOpen ? "▾" : "▸"}
                </span>
                <span className="xg-progress-tab__orch-text" data-testid="rightbar-progress-orch-summary">
                  编排步骤 {orch.count} 步{orchLatestText ? ` · 最近 ${orchLatestText}` : ""}
                </span>
                <span className="xg-progress-tab__orch-action">{orchOpen ? "收起" : "展开"}</span>
              </button>

              {orchOpen ? (
                <div className="xg-progress-tab__orch-body" data-testid="rightbar-progress-orch-body">
                  <ul className="xg-progress-tab__orch-list" data-testid="rightbar-progress-orch-list">
                    {orch.rows.map((r, i) => (
                      <li
                        key={`${i}:${r.stepNo}:${r.at}`}
                        className="xg-progress-tab__orch-row"
                        data-orch-step={r.stepNo === null ? "" : String(r.stepNo)}
                      >
                        <span className="xg-progress-tab__orch-at">{fmtAt(r.at)}</span>
                        <span className="xg-progress-tab__orch-no">{r.stepNo === null ? "步骤 ?" : `#${r.stepNo}`}</span>
                        <span className="xg-progress-tab__orch-tool">{toolTextOf(r)}</span>
                        {r.ok !== null ? (
                          <span className={"xg-progress-tab__orch-state" + (r.ok === false ? " xg-progress-tab__orch-state--bad" : "")}>
                            {r.ok ? "成功" : "失败"}
                          </span>
                        ) : null}
                        {r.error ? (
                          <span className="xg-progress-tab__orch-err" title={r.error}>
                            {r.error}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  <p className="xg-progress-tab__orch-count" data-testid="rightbar-progress-orch-count">
                    共 {orch.count} 步{orch.truncated ? `（仅显示最近 ${orch.maxRows} 步）` : ""}
                  </p>
                  {orch.degraded > 0 ? (
                    <p className="xg-progress-tab__orch-degraded" data-testid="rightbar-progress-orch-degraded">
                      （{orch.degraded} 条步骤载荷非 JSON，未解析正文）
                    </p>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}

          {/* 空集 ⇒ 如实说明 */}
          {items.length === 0 ? (
            <p className="xg-progress-tab__hint" data-testid="rightbar-progress-empty">
              暂无节点动作
            </p>
          ) : (
            <ul className="xg-progress-tab__list" data-testid="rightbar-progress-list">
              {items.map((it) => (
                <li
                  key={it.key}
                  className="xg-progress-tab__row"
                  data-node={it.node}
                  data-node-event={it.label}
                  data-node-event-type={it.action || it.label}
                >
                  <span className="xg-progress-tab__at">{it.atText}</span>
                  <span className="xg-progress-tab__node">{it.node}</span>
                  <span className="xg-progress-tab__label">{it.label}</span>
                  {it.detail ? (
                    <span className="xg-progress-tab__detail" title={it.detail}>
                      {it.detail}
                    </span>
                  ) : null}
                  {it.errorCode ? <span className="xg-progress-tab__code">{it.errorCode}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <style>{STYLE}</style>
    </div>
  );
}

/** 自包含样式（源件 `<style scoped>` 逐字译入；零字面色：一律消费 L2 令牌（design-guard G1/G2）） */
import { progressTabBodyStyle as STYLE } from "../../styles/inlineStyles";

export default ProgressTabBody;
